import { useCallback, useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes } from "react-router-dom";
import { ClerkFailed, ClerkLoading, Show, useAuth, useClerk } from "@clerk/react";
import { Toaster } from "sonner";
import "@/App.css";
import "@/styles/ops.css";
import "@/styles/ops2.css";
import "@/styles/tenancy.css";
import "@/styles/extras.css";
import JoinLink from "@/pages/JoinLink";
import ClerkAuthProvider from "@/components/auth/ClerkAuthProvider";
import Shell from "@/components/Shell";
import { api, errMsg, MemberContext, setTokenGetter } from "@/lib/api";
import AuthPage from "@/pages/AuthPage";
import Dashboard from "@/pages/Dashboard";
import VoiceEntry from "@/pages/VoiceEntry";
import Patients from "@/pages/Patients";
import PatientProfile from "@/pages/PatientProfile";
import ClinicalRecords from "@/pages/ClinicalRecords";
import TreatmentLedger from "@/pages/TreatmentLedger";
import Payments from "@/pages/Payments";
import Team from "@/pages/Team";
import Cashbook from "@/pages/Cashbook";
import Appointments from "@/pages/Appointments";
import Settings from "@/pages/Settings";
import Laboratory from "@/pages/Laboratory";
import Expenses from "@/pages/Expenses";
import Reports from "@/pages/Reports";
import ModulePage from "@/pages/ModulePage";
import { Onboarding, PendingAccess } from "@/pages/Onboarding";

const Guard = ({ member, perm, children }) => (member.permissions.includes(perm) ? children : <Navigate to="/" replace />);

function Workspace() {
  const { getToken } = useAuth();
  const { signOut } = useClerk();
  const [member, setMember] = useState(null);
  const [error, setError] = useState("");
  setTokenGetter(getToken);
  const load = useCallback(() => api.get("/me").then((r) => setMember(r.data)).catch((e) => setError(errMsg(e, "Could not load your profile"))), []);
  useEffect(() => { load(); }, [load]);
  if (error) return <div className="content"><div className="error" data-testid="workspace-error">{error}</div></div>;
  if (!member) return <div className="content"><p className="muted" data-testid="workspace-loading">Opening your workspace…</p></div>;
  if (member.status === "onboarding") return <Onboarding member={member} onDone={load} onSignOut={() => signOut({ redirectUrl: "/" })} />;
  if (member.status !== "active") return <PendingAccess member={member} onDone={load} onSignOut={() => signOut({ redirectUrl: "/" })} />;
  localStorage.removeItem("df_join_code"); localStorage.removeItem("df_signup_intent");
  const g = (perm, el) => <Guard member={member} perm={perm}>{el}</Guard>;
  return (
    <MemberContext.Provider value={member}>
      <Shell>
        <Routes>
          <Route path="/" element={<Dashboard />} />
          <Route path="/voice" element={g("voice", <VoiceEntry />)} />
          <Route path="/patients" element={<Patients />} />
          <Route path="/patients/:id" element={<PatientProfile />} />
          <Route path="/clinical" element={g("clinical:read", <ClinicalRecords />)} />
          <Route path="/treatments" element={g("ledger:read", <TreatmentLedger />)} />
          <Route path="/payments" element={g("ledger:read", <Payments />)} />
          <Route path="/team" element={g("team", <Team />)} />
          <Route path="/cashbook" element={g("cashbook:read", <Cashbook />)} />
          <Route path="/appointments" element={g("appointments:read", <Appointments />)} />
          <Route path="/lab" element={g("lab:read", <Laboratory />)} />
          <Route path="/expenses" element={g("expenses:read", <Expenses />)} />
          <Route path="/reports" element={g("reports:read", <Reports />)} />
          <Route path="/settings" element={g("settings:write", <Settings />)} />
          <Route path="/modules/:slug" element={<ModulePage />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Shell>
    </MemberContext.Provider>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ClerkAuthProvider>
        <Toaster position="top-right" richColors />
        <Routes>
          <Route path="/sign-in/*" element={<AuthPage mode="in" />} />
          <Route path="/join/:code" element={<JoinLink />} />
          <Route path="/sign-up/*" element={<AuthPage mode="up" />} />
          <Route path="/*" element={<><Show when="signed-in"><Workspace /></Show><Show when="signed-out"><Navigate to="/sign-in" replace /></Show></>} />
        </Routes>
        <ClerkLoading><p className="boot-note" data-testid="app-loading">Opening DentaflowAI…</p></ClerkLoading>
        <ClerkFailed><div className="boot-note" data-testid="sign-in-failed"><b>Sign-in could not load on this address.</b><span>Please open the app at your clinic's main web address, then refresh.</span><button className="primary" onClick={() => window.location.reload()} data-testid="sign-in-retry-button">Refresh</button></div></ClerkFailed>
      </ClerkAuthProvider>
    </BrowserRouter>
  );
}
